import WeekDayHeader from './WeekDayHeader';
import { useEffect, useMemo, useRef } from 'react';
import { getMonthsBetweenDates } from './CalendarRange';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { isSameMonth } from 'date-fns';
import Divider from '../Divider';
import CalendarHeader from './CalendarHeader';
import CalendarMonth from './CalendarMonth';

const Constants = {
  MonthHeightEstimated: 375
};

type MultipleMonthCalendarProps = {
  initialMonth: Date;
  selectedDate?: Date;
  onSelect?: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
};

type MonthRowData = {
  months: Date[];
  selectedDate?: Date;
  onSelect?: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
};

const getMonthRowKey = (index: number, data: MonthRowData) => data.months[index].toISOString();

const MonthRow = ({ index, style, data }: ListChildComponentProps<MonthRowData>) => {
  const month = data.months[index];

  return (
    <div style={style} className="px-4">
      <div className="h-full pt-8">
        <CalendarHeader currentDate={month} minDate={data.minDate} maxDate={data.maxDate} />
        <CalendarMonth
          currentDate={month}
          selectedDate={data.selectedDate}
          onSelect={data.onSelect}
          minDate={data.minDate}
          maxDate={data.maxDate}
        />
      </div>
      <Divider color="light" />
    </div>
  );
};

const MultipleMonthCalendar = ({
  initialMonth,
  selectedDate,
  onSelect,
  minDate,
  maxDate
}: MultipleMonthCalendarProps): JSX.Element => {
  const listRef = useRef<FixedSizeList>(null);

  const months = useMemo(() => getMonthsBetweenDates(minDate, maxDate), [maxDate, minDate]);

  const rowData = useMemo(
    () => ({
      months,
      selectedDate,
      onSelect,
      minDate,
      maxDate
    }),
    [maxDate, minDate, months, onSelect, selectedDate]
  );

  useEffect(() => {
    const initialIndex = months.findIndex(month => isSameMonth(month, initialMonth));

    const timer = setTimeout(() => {
      if (initialIndex > 0) {
        listRef.current?.scrollToItem(initialIndex, 'start');
      }
    }, 10);

    return () => {
      clearTimeout(timer);
    };
  }, [initialMonth, months]);

  return (
    <div className="absolute size-full pb-16">
      <div className="mt-4 px-4">
        <WeekDayHeader />
        <Divider color="light" className="mt-4" />
      </div>
      <AutoSizer>
        {({ height, width }) => (
          <FixedSizeList
            height={height}
            itemCount={months.length}
            itemData={rowData}
            itemKey={getMonthRowKey}
            itemSize={Constants.MonthHeightEstimated}
            width={width}
            ref={listRef}
          >
            {MonthRow}
          </FixedSizeList>
        )}
      </AutoSizer>
    </div>
  );
};

export default MultipleMonthCalendar;
